import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import {
  Users, Building2, AppWindow, ShieldAlert, Activity, UserCheck, CreditCard, CheckCircle2, Briefcase, ExternalLink, Play, GripVertical, Newspaper, LifeBuoy, Pin, Pencil, Check, X, Plus, LockKeyhole, Globe, ShieldCheck, LayoutGrid, List, Rows3, Compass, ChevronUp, ChevronDown, Clock, BellOff, Eye,
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { AvatarNudge } from "@/components/AvatarNudge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RoleBadge } from "@/components/Badges";
import { ReleaseNotesCard } from "@/components/ReleaseNotesCard";
import { CelebrationsWidget } from "@/components/CelebrationsWidget";
import { PriceChangesCard } from "@/components/pricelist/PriceChangesCard";
import { EmailKillSwitch } from "@/components/EmailKillSwitch";
// Iter 90c — CommissionsWidget & PartnerReadinessWidget now live on the
// Partners page; ResumeHealthWidget lives on the Job Board page.
import { ActivityFeed } from "@/components/ActivityFeed";

// Google Material Symbols mapping — used by DynIcon so the ~10 apps that
// don't have a custom `icon_url` uploaded still render a consistent
// filled Google-style icon (matching the base64 PNGs stored for the
// other tiles) instead of a small primary-tinted lucide outline.
// Iter 107 — reported by Keith as an inconsistency in the app grid.
const MATERIAL_ICON_MAP = {
  AppWindow: "apps",
  LayoutDashboard: "dashboard",
  Home: "home",
  FileText: "description",
  Printer: "print",
  DollarSign: "payments",
  Briefcase: "work",
  Bell: "notifications",
  Hotel: "hotel",
  HeartHandshake: "volunteer_activism",
  LifeBuoy: "support_agent",
  Users: "group",
  User: "person",
  Building2: "apartment",
  Activity: "monitoring",
  UserCheck: "how_to_reg",
  CreditCard: "credit_card",
  CheckCircle2: "check_circle",
  ExternalLink: "open_in_new",
  Play: "play_arrow",
  Newspaper: "newspaper",
  Pin: "push_pin",
  Pencil: "edit",
  ShieldCheck: "verified_user",
  ShieldAlert: "gpp_maybe",
  Globe: "public",
  Compass: "explore",
  Sparkles: "auto_awesome",
  Package: "inventory_2",
  Truck: "local_shipping",
  Wrench: "build",
  Settings: "settings",
  BookOpen: "menu_book",
  Book: "book",
  Phone: "call",
  MessageCircle: "chat",
  MessageSquare: "chat_bubble",
  Mail: "mail",
  Calendar: "calendar_month",
  Search: "search",
  Filter: "filter_alt",
  Star: "star",
  BarChart: "bar_chart",
  BarChart3: "bar_chart",
  PieChart: "pie_chart",
  TrendingUp: "trending_up",
  ShoppingCart: "shopping_cart",
  Zap: "bolt",
  Cloud: "cloud",
  Database: "database",
  Server: "dns",
  Terminal: "terminal",
  Cpu: "memory",
  Wifi: "wifi",
  Map: "map",
  MapPin: "location_on",
  Clock: "schedule",
  Timer: "timer",
  AlertCircle: "error",
  AlertTriangle: "warning",
  Info: "info",
  HelpCircle: "help",
  Key: "key",
  KeyRound: "key",
  Lock: "lock",
  LockKeyhole: "lock",
  Palette: "palette",
  Plug: "power",
  ScrollText: "receipt_long",
  GitPullRequest: "call_merge",
  BookOpenCheck: "menu_book",
};

// Map the caller-supplied Tailwind `h-*` class to a matching pixel font
// size so the material-symbol renders at roughly the same visual weight
// as the equivalent lucide icon it replaces.
const MS_SIZE_PX = { "h-3.5": 32, "h-4": 32, "h-5": 40, "h-6": 48, "h-7": 56, "h-8": 64 };

function DynIcon({ name, className = "" }) {
  const materialName = MATERIAL_ICON_MAP[name];
  if (materialName) {
    const found = Object.entries(MS_SIZE_PX).find(([cls]) => className.includes(cls));
    const px = found ? found[1] : 24;
    return (
      <span
        className={`material-symbols-rounded inline-flex items-center justify-center leading-none ${className}`}
        style={{
          fontSize:               `${px}px`,
          width:                  `${px}px`,
          height:                 `${px}px`,
          fontVariationSettings:  "'FILL' 1, 'wght' 500, 'GRAD' 0, 'opsz' 40",
        }}
        aria-hidden="true">
        {materialName}
      </span>
    );
  }
  const C = Icons[name] || AppWindow;
  return <C className={className} />;
}

// 3-way lifecycle stage — falls back to legacy `show_roadmap_badge` bool.
const ROADMAP_STAGES = {
  roadmap: { label: "Roadmap", bg: "bg-emerald-600" },
  on_deck: { label: "On Deck", bg: "bg-amber-500"   },
  future:  { label: "Future",  bg: "bg-orange-500"  },
};
export function resolveRoadmapStage(a) {
  const s = (a?.roadmap_stage || "").toLowerCase();
  if (ROADMAP_STAGES[s]) return s;
  return a?.show_roadmap_badge ? "roadmap" : "";
}
function RoadmapBadge({ stage, testid, className = "" }) {
  const meta = ROADMAP_STAGES[stage];
  if (!meta) return null;
  return (
    <span data-testid={testid}
          className={`rounded-full ${meta.bg} px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none text-white shadow ${className}`}>
      {meta.label}
    </span>
  );
}

// Iter 78: New / Beta / Roadmap-stage are mutually exclusive and render
// as a SINGLE pill in the top-right. Priority: New > Beta > Stage.
function pickTileBadge(a) {
  if (a?.show_new_badge)  return { kind: "new",  label: "New",  bg: "bg-red-600"  };
  if (a?.show_beta_badge) return { kind: "beta", label: "Beta", bg: "bg-blue-600" };
  const s = resolveRoadmapStage(a);
  if (s) {
    const meta = ROADMAP_STAGES[s];
    return { kind: `stage-${s}`, label: meta.label, bg: meta.bg };
  }
  return null;
}

// Iter 90h — SecurityBadge moved to /components/SecurityBadge.jsx and
// now lives in the global top nav (Layout.jsx).

// Kept outside <Dashboard/> so its identity is stable across renders — this
// is what makes drag & drop reliable (a re-mounted tile mid-drag cancels
// the drop event, which is the historical bug).
const AppTile = ({ a, locked, editMode, isHidden, canDrag, sdOpenCount, hdOpenCount, hdOverdue,
                  onLaunch, onDragStart, onDragOver, onDragLeave, onDrop, onDragEnd,
                  onHide, onShow, dragOverId, isSuperAdmin, onQuickEdit,
                  isBulkSelected }) => (
  <button
    data-testid={`dashboard-app-${a.app_id}`}
    onClick={() => { if (!editMode) onLaunch(a); }}
    draggable={canDrag}
    onDragStart={onDragStart}
    onDragOver={onDragOver}
    onDragLeave={onDragLeave}
    onDrop={onDrop}
    onDragEnd={onDragEnd}
    title={a.name}
    className={`group relative flex flex-col items-center gap-1 rounded-xl p-1 text-center transition-all hover:bg-slate-50 ${dragOverId === a.app_id ? "ring-2 ring-primary/40" : ""} ${isBulkSelected ? "ring-2 ring-indigo-500 bg-indigo-50/60" : ""} ${canDrag ? "cursor-grab active:cursor-grabbing" : ""} ${editMode && isHidden ? "opacity-50" : ""}`}
  >
    <span className={`relative flex aspect-square w-2/3 items-center justify-center overflow-hidden rounded-xl border shadow-sm transition-all group-hover:shadow-md ${locked ? "border-amber-200 bg-amber-50 text-amber-500" : "border-slate-200 bg-white text-primary group-hover:border-primary/40"}`}>
      {(() => {
        return (
          <>
            {/* Iter 110x — Uniform icon rendering. All app icons now
                sit on the same white/rounded-xl bordered chip, use
                `object-contain` (no more stretched/cropped PNGs) and
                get 20% padding to normalise sizing. DynIcon uses the
                brand primary green so it visually matches the many
                green-branded uploaded icons.
                Iter 110y — By default, uploaded PNGs also get a
                `grayscale + brightness` filter so branded green/blue
                icons don't fight the tile aesthetic. Ops can opt
                out per-app via `preserve_icon_color` in the Apps
                editor (e.g. for vendor logos with load-bearing
                colour like Stripe or Zoom). */}
            {a.icon_url
              ? <img src={a.icon_url} alt={a.name} className={`h-4/5 w-4/5 object-contain ${!a.preserve_icon_color ? "app-icon-monochrome" : ""} ${locked ? "opacity-40 grayscale" : ""}`} />
              : <DynIcon name={a.icon} className="h-6 w-6 text-primary" />}
            {locked && <span className="absolute inset-0 flex items-center justify-center bg-white/50"><LockKeyhole className="h-4 w-4 text-amber-600" /></span>}
          </>
        );
      })()}
      {canDrag && <GripVertical className="absolute right-0.5 top-0.5 h-3 w-3 text-slate-300 opacity-0 group-hover:opacity-100" />}
      {!editMode && !locked && (() => { const b = pickTileBadge(a); return b ? <span data-testid={`dashboard-app-badge-${a.app_id}`} className={`absolute right-0.5 top-0.5 rounded-full ${b.bg} px-1 py-0.5 text-[8px] font-bold uppercase leading-none text-white shadow`}>{b.label}</span> : null; })()}
      {/* Iter 91ak — unseen version dot. Sits opposite the badge so both
          can be visible; if the badge is already at top-right and there
          IS an unseen update, we tuck the dot at top-LEFT. Only shows on
          full/pill tiles (compact has its own mini-dot below). */}
      {!editMode && !locked && a.has_unseen_update && (
        <span
          data-testid={`dashboard-app-updated-${a.app_id}`}
          title={`New version available${a.version ? ` (v${a.version})` : ""}`}
          className="absolute left-0.5 top-0.5 h-2 w-2 rounded-full bg-red-500 shadow ring-2 ring-white"
        />
      )}
      {a.route === "/service-desk" && sdOpenCount > 0 && !editMode && !locked && <span data-testid={`dashboard-app-sd-count-${a.app_id}`} title={`${sdOpenCount} open Intercom conversation(s)`} className="absolute right-0.5 top-0.5 rounded-full bg-primary px-1.5 py-0.5 text-[9px] font-bold leading-none text-white shadow">{sdOpenCount > 99 ? "99+" : sdOpenCount}</span>}
      {a.route === "/helpdesk" && hdOpenCount > 0 && !editMode && !locked && (
        <span data-testid={`dashboard-app-hd-count-${a.app_id}`}
              title={hdOverdue > 0 ? `${hdOverdue} overdue of ${hdOpenCount} open helpdesk ticket(s)` : `${hdOpenCount} open helpdesk ticket(s)`}
              className={`absolute right-0.5 top-0.5 rounded-full ${hdOverdue > 0 ? "bg-rose-600" : "bg-primary"} px-1.5 py-0.5 text-[9px] font-bold leading-none text-white shadow`}>
          {hdOpenCount > 99 ? "99+" : hdOpenCount}
        </span>
      )}
      {editMode && (
        <span role="button" data-testid={isHidden ? `dashboard-app-add-${a.app_id}` : `dashboard-app-hide-${a.app_id}`}
          onClick={(e) => { e.stopPropagation(); isHidden ? onShow(a.app_id) : onHide(a.app_id); }}
          className={`absolute left-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full text-white shadow ${isHidden ? "bg-primary hover:bg-primary/90" : "bg-red-500 hover:bg-red-600"}`}>
          {isHidden ? <Plus className="h-2.5 w-2.5" /> : <X className="h-2.5 w-2.5" />}
        </span>
      )}
    </span>
    <span className="block w-full truncate text-[10px] font-bold leading-tight text-slate-700">{a.name}</span>
    {/* Iter 90h — super-admin-only quick-edit pencil, absolute pinned
        so it doesn't shift the tile's layout. Stops the tile's launch
        click from firing. */}
    {isSuperAdmin && !editMode && (
      <span role="button" tabIndex={0}
            data-testid={`dashboard-app-quickedit-${a.app_id}`}
            title={`Edit "${a.name}" configuration`}
            onClick={(e) => { e.stopPropagation(); onQuickEdit(a.app_id, e); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onQuickEdit(a.app_id, e); } }}
            className="absolute right-1 bottom-1 flex h-5 w-5 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 opacity-0 shadow-sm transition group-hover:opacity-100 hover:bg-primary hover:text-white hover:border-primary">
        <Pencil className="h-3 w-3" />
      </span>
    )}
  </button>
);

// Compact mode — small icon-only square with a tiny label underneath.
// Perfect when Keith wants to see everything at a glance while still
// being able to identify each tile without a tooltip hover.
const AppTileCompact = ({ a, sdOpenCount, hdOpenCount, hdOverdue, onLaunch, isSuperAdmin, onQuickEdit, isBulkSelected }) => (
  <button data-testid={`dashboard-app-${a.app_id}`} title={a.name}
    onClick={() => onLaunch(a)}
    className={`group relative flex flex-col items-center justify-center gap-1 rounded-lg border bg-white px-2 py-2 shadow-sm transition-all hover:shadow-md ${isBulkSelected ? "border-indigo-500 ring-2 ring-indigo-300 bg-indigo-50/60" : "border-slate-200 hover:border-primary/40"}`}>
    <span className="relative flex h-9 w-9 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      {a.icon_url
        ? <img src={a.icon_url} alt={a.name} className={`h-4/5 w-4/5 object-contain ${!a.preserve_icon_color ? "app-icon-monochrome" : ""}`} />
        : <DynIcon name={a.icon} className="h-5 w-5 text-primary" />}
      {a.has_unseen_update && (
        <span data-testid={`dashboard-app-updated-${a.app_id}`}
              title={`New version available${a.version ? ` (v${a.version})` : ""}`}
              className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-red-500 shadow ring-2 ring-white" />
      )}
      {a.route === "/service-desk" && sdOpenCount > 0 && (
        <span className="absolute -right-1 -top-1 rounded-full bg-primary px-1 py-0.5 text-[9px] font-bold leading-none text-white shadow" data-testid={`dashboard-app-sd-count-${a.app_id}`}>{sdOpenCount > 99 ? "99+" : sdOpenCount}</span>
      )}
      {a.route === "/helpdesk" && hdOpenCount > 0 && (
        <span className={`absolute -right-1 -top-1 rounded-full ${hdOverdue > 0 ? "bg-rose-600" : "bg-primary"} px-1 py-0.5 text-[9px] font-bold leading-none text-white shadow`}
              data-testid={`dashboard-app-hd-count-${a.app_id}`}>
          {hdOpenCount > 99 ? "99+" : hdOpenCount}
        </span>
      )}
    </span>
    {/* Iter 87e: tiny label so compact users don't need tooltip hover
        to identify a tile. Truncates cleanly on long names. */}
    <span data-testid={`dashboard-app-label-compact-${a.app_id}`}
          className="max-w-[80px] truncate text-[10px] font-medium text-slate-700 leading-tight text-center">
      {a.name}
    </span>
    <span className="absolute -top-1 -right-1">{(() => { const b = pickTileBadge(a); return b ? <span data-testid={`dashboard-app-badge-compact-${a.app_id}`} className={`rounded-full ${b.bg} px-1 py-0.5 text-[8px] font-bold uppercase leading-none text-white shadow`}>{b.label}</span> : null; })()}</span>
    {isSuperAdmin && (
      <span role="button" tabIndex={0}
            data-testid={`dashboard-app-quickedit-${a.app_id}`}
            title={`Edit "${a.name}" configuration`}
            onClick={(e) => { e.stopPropagation(); onQuickEdit(a.app_id, e); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onQuickEdit(a.app_id, e); } }}
            className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 opacity-0 shadow transition group-hover:opacity-100 hover:bg-primary hover:text-white hover:border-primary">
        <Pencil className="h-2.5 w-2.5" />
      </span>
    )}
  </button>
);

// Pill list mode — single horizontal row per app, ideal for scanning
// long lists top-to-bottom without hunting.
const AppTilePill = ({ a, sdOpenCount, hdOpenCount, hdOverdue, onLaunch, isSuperAdmin, onQuickEdit, isBulkSelected }) => (
  <button data-testid={`dashboard-app-${a.app_id}`} onClick={() => onLaunch(a)}
    className={`group relative flex w-full items-start gap-3 rounded-2xl border bg-white px-3 py-2.5 text-left shadow-sm transition-all hover:shadow-md ${isBulkSelected ? "border-indigo-500 ring-2 ring-indigo-300 bg-indigo-50/60" : "border-slate-200 hover:border-primary/40 hover:bg-slate-50"}`}>
    <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm relative">
      {a.icon_url
        ? <img src={a.icon_url} alt={a.name} className={`h-4/5 w-4/5 object-contain ${!a.preserve_icon_color ? "app-icon-monochrome" : ""}`} />
        : <DynIcon name={a.icon} className="h-5 w-5 text-primary" />}
      {a.has_unseen_update && (
        <span data-testid={`dashboard-app-updated-${a.app_id}`}
              title={`New version available${a.version ? ` (v${a.version})` : ""}`}
              className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-red-500 shadow ring-2 ring-white" />
      )}
    </span>
    {/* Iter 90m — title on its own line at full width so long names
        (Service Desk, SIP Trace Analysis, Billing Portal (SSO available))
        stop truncating. Description sits beneath. Badges + Play arrow are
        pushed to the top-right corner so they never squeeze the title. */}
    <span className="flex min-w-0 flex-1 flex-col pr-8">
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-sm font-semibold text-slate-800 leading-snug break-words">{a.name}</span>
        {(() => { const b = pickTileBadge(a); return b ? <span data-testid={`dashboard-app-badge-pill-${a.app_id}`} className={`rounded-full ${b.bg} px-1.5 py-0.5 text-[9px] font-bold uppercase text-white`}>{b.label}</span> : null; })()}
        {a.route === "/service-desk" && sdOpenCount > 0 && (
          <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-white" data-testid={`dashboard-app-sd-count-${a.app_id}`}>{sdOpenCount > 99 ? "99+" : sdOpenCount}</span>
        )}
        {a.route === "/helpdesk" && hdOpenCount > 0 && (
          <span className={`rounded-full ${hdOverdue > 0 ? "bg-rose-600" : "bg-primary"} px-2 py-0.5 text-[10px] font-bold text-white`}
                data-testid={`dashboard-app-hd-count-${a.app_id}`}
                title={hdOverdue > 0 ? `${hdOverdue} overdue of ${hdOpenCount} open` : `${hdOpenCount} open tickets`}>
            {hdOpenCount > 99 ? "99+" : hdOpenCount}
          </span>
        )}
      </span>
      {a.description && <span className="mt-0.5 text-xs text-slate-500 leading-snug break-words">{a.description}</span>}
    </span>
    <Play className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-300 group-hover:text-primary" />
    {isSuperAdmin && (
      <span role="button" tabIndex={0}
            data-testid={`dashboard-app-quickedit-${a.app_id}`}
            title={`Edit "${a.name}" configuration`}
            onClick={(e) => { e.stopPropagation(); onQuickEdit(a.app_id, e); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onQuickEdit(a.app_id, e); } }}
            className="absolute right-2 bottom-2 flex h-5 w-5 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 opacity-0 shadow-sm transition group-hover:opacity-100 hover:bg-primary hover:text-white hover:border-primary">
        <Pencil className="h-3 w-3" />
      </span>
    )}
  </button>
);

function applyOrder(list, order) {
  if (!order?.length) return list;
  const rank = new Map(order.map((id, i) => [id, i]));
  return [...list].sort((a, b) => {
    const ra = rank.has(a.app_id) ? rank.get(a.app_id) : Infinity;
    const rb = rank.has(b.app_id) ? rank.get(b.app_id) : Infinity;
    return ra - rb;
  });
}

export default function Dashboard() {
  const { user, has, logout } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [info, setInfo] = useState(null);
  const [news, setNews] = useState([]);
  // Iter 110t — Dashboard header banner dismissal (JSON with hash of
  // the current banner payload + optional snooze-until epoch ms).
  //   { hash: <str>, until: <null | msEpoch> }
  // Iter 110v — `until=null` means "hide until Marketing edits the
  // banner" (permanent dismiss). `until=<future>` means "snooze"; the
  // banner auto-reopens when now() > until.
  const [dashboardHeaderDismissed, setDashboardHeaderDismissed] = useState(() => {
    try {
      const raw = localStorage.getItem("conduit.dashboard-header.dismissed.v1");
      if (!raw) return null;
      // Legacy value written by Iter 110t was a plain string hash.
      // If we see a bare string we upgrade it into the object shape
      // so the guard below can keep working uniformly.
      if (raw.startsWith("{")) return JSON.parse(raw);
      return { hash: raw, until: null };
    } catch { return null; }
  });
  const [sdOpenCount, setSdOpenCount] = useState(0);
  const [hdOpenCount, setHdOpenCount] = useState(0);
  const [hdOverdue,   setHdOverdue]   = useState(0);
  const canManageApps = has("apps.edit");
  const dragId = useRef(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [hidden, setHidden] = useState([]);
  const hiddenKey = user?.user_id ? `dash_hidden_${user.user_id}` : null;

  // Internal-Only gating
  const [access, setAccess] = useState({ whitelisted: true, enforced: false, ip: "", is_super_admin: false, can_manage: false });
  // Applications sits ABOVE Internal Only by default (Iter 74 rename +
  // reorder). Users who saved a custom order via /me/section-order keep it.
  const [sectionOrder, setSectionOrder] = useState(["shared", "internal", "roadmap"]);
  // Iter 90d — per-user collapsed state, persisted via localStorage.
  const [sectionCollapsed, setSectionCollapsed] = useState({});
  const [sectionDrag, setSectionDrag] = useState(null);
  // App-tile view mode — persisted per-user in localStorage. Three modes:
  // "full" (card look), "compact" (small square buttons, no text),
  // "pill" (horizontal rows with name + description). Default is "pill"
  // (List) per Feb 2026 UX call.
  const viewKey = user?.user_id ? `dash_view_${user.user_id}` : null;
  const [viewMode, setViewModeState] = useState("pill");
  useEffect(() => {
    if (!viewKey) return;
    const saved = localStorage.getItem(viewKey);
    // Iter 79: "Full" mode removed — migrate stale saves to "pill".
    if (saved === "compact" || saved === "pill") setViewModeState(saved);
    else if (saved === "full") { setViewModeState("pill"); localStorage.setItem(viewKey, "pill"); }
  }, [viewKey]);
  const setViewMode = (m) => {
    setViewModeState(m);
    if (viewKey) localStorage.setItem(viewKey, m);
  };
  // Security status (staff/super only)
  // Iter 90h — Security status now lives in Layout.jsx (top nav chip).
  // Keep the state out of Dashboard entirely.
  // Partner News editor gate — matches backend `news_editor` in misc.py.
  const isNewsEditor = user?.role === "super_admin"
    || (user?.base_role === "staff" && (user?.team || "").toLowerCase() === "marketing");
  // Per-user override: { app_id: "internal" | "shared" }. Wins over the
  // app's global `internal_only`. Saved via PUT /me/section-overrides so
  // it never touches the platform default (which stays in Settings → Apps).
  const [sectionOverrides, setSectionOverrides] = useState({});
  const [logoutIn, setLogoutIn] = useState(null); // seconds remaining, or null
  const timerRef = useRef(null);

  const loadAccess = () => api.get("/internal-access").then((r) => setAccess(r.data)).catch(() => {});

  useEffect(() => {
    Promise.all([
      api.get("/apps").then((r) => (r.data || []).filter((a) => a.status === "active" && a.show_on_dashboard !== false)).catch(() => []),
      api.get("/me/dashboard-order").then((r) => r.data?.order || []).catch(() => []),
    ]).then(([list, order]) => setApps(applyOrder(list, order)));
    api.get("/me/section-order").then((r) => {
      // Iter 90d migration — added "roadmap" as a third section holding
      // any app with a badge other than "New". Backfill saved orders so
      // roadmap always renders (as the last section by default).
      const saved = r.data?.order;
      const NEW_DEFAULT = ["shared", "internal", "roadmap"];
      if (!saved || saved.length === 0
          || (saved.length === 2 && saved[0] === "internal" && saved[1] === "shared")) {
        setSectionOrder(NEW_DEFAULT);
        api.put("/me/section-order", { order: NEW_DEFAULT }).catch(() => {});
      } else if (!saved.includes("roadmap")) {
        const next = [...saved, "roadmap"];
        setSectionOrder(next);
        api.put("/me/section-order", { order: next }).catch(() => {});
      } else {
        setSectionOrder(saved);
      }
    }).catch(() => {});
    api.get("/me/section-overrides").then((r) => setSectionOverrides(r.data?.overrides || {})).catch(() => {});
    loadAccess();
    api.get("/dashboard/info").then((r) => setInfo(r.data)).catch(() => setInfo(null));
    api.get("/announcements").then((r) => setNews(r.data)).catch(() => setNews([]));
    // Iter 90h — security status is fetched by Layout.jsx (top nav badge)
    // so we no longer duplicate that call here.
    if (has("service_desk.view")) api.get("/intercom/open-count").then((r) => setSdOpenCount(r.data?.count || 0)).catch(() => {});
    // Iter 107c — Live Tile Counter for the Helpdesk app.
    if (has("helpdesk.view")) api.get("/helpdesk/stats").then((r) => {
      setHdOpenCount(r.data?.open || 0);
      setHdOverdue(r.data?.overdue || 0);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!hiddenKey) return;
    try { setHidden(JSON.parse(localStorage.getItem(hiddenKey)) || []); } catch { setHidden([]); }
  }, [hiddenKey]);

  // Iter 90d — restore collapsed sections per user from localStorage.
  // Iter 90f — force "shared" (Applications) to always start expanded.
  useEffect(() => {
    const key = `dash_sections_collapsed_${user?.user_id || "anon"}`;
    try {
      const v = JSON.parse(localStorage.getItem(key) || "{}");
      if (v && typeof v === "object") {
        delete v.shared;
        setSectionCollapsed(v);
      }
    } catch { setSectionCollapsed({}); }
  }, [user?.user_id]);

  // Auto-logout countdown (started when a non-whitelisted user opens an Internal-Only app).
  useEffect(() => {
    if (logoutIn === null) return;
    if (logoutIn <= 0) { logout(); return; }
    timerRef.current = setTimeout(() => setLogoutIn((s) => (s === null ? null : s - 1)), 1000);
    return () => clearTimeout(timerRef.current);
  }, [logoutIn, logout]);
  const cancelLogout = () => { setLogoutIn(null); if (timerRef.current) clearTimeout(timerRef.current); };

  const persistHidden = (next) => {
    setHidden(next);
    if (hiddenKey) localStorage.setItem(hiddenKey, JSON.stringify(next));
  };
  const hideApp = (id) => persistHidden([...hidden, id]);
  const showApp = (id) => persistHidden(hidden.filter((x) => x !== id));

  const startLogoutTimer = () => { if (logoutIn === null) setLogoutIn(300); };

  const whitelistMyIp = async () => {
    try {
      if (access.can_manage) { await api.post("/internal-ip/add", { ip: access.ip }); toast.success("Your IP is now whitelisted"); }
      else { await api.post("/internal-ip/request"); toast.success("Whitelist request sent to an admin"); }
      cancelLogout(); loadAccess();
    } catch (e) { toast.error("Could not whitelist right now"); }
  };

  const launchApp = (app) => {
    const internalBlocked = app.internal_only && !access.whitelisted;
    if (internalBlocked) { startLogoutTimer(); toast.error("This internal app is locked on your current network."); return; }
    // Iter 91ak — silently clear the "new version" dot when the user
    // launches the app. Fire and forget — no need to block navigation.
    if (app.has_unseen_update) {
      api.post(`/apps/${app.app_id}/mark-seen`).catch(() => {});
    }
    if (app.app_type === "native" && app.route) { navigate(app.route); return; }
    // Native fallback by key — lets us upgrade apps to first-party routes
    // without changing their DB records first.
    const NATIVE_ROUTES = { reports: "/reports" };
    const nativeRoute = NATIVE_ROUTES[(app.key || app.app_key || "").toLowerCase()]
      || NATIVE_ROUTES[(app.name || "").toLowerCase()];
    if (nativeRoute) { navigate(nativeRoute); return; }
    // Iter 110n — Native-render apps (rendered inline by AppViewer via
    // slug — see AppViewer.jsx line ~285). These have no external `url`
    // and don't need `embed_allowed`, so route straight to the viewer
    // and skip the URL-required check below.
    const NATIVE_RENDER_SLUGS = new Set(["audit-log", "status-page"]);
    if (NATIVE_RENDER_SLUGS.has(app.slug)) {
      navigate(`/apps/${app.slug}`);
      return;
    }
    if (!app.embed_allowed) {
      const url = (app.url || "").trim();
      if (!url || url === "https:" || url === "http:" || !/^https?:\/\/.+/i.test(url)) {
        toast.error(`${app.name} has no URL configured yet — ask an admin to set it in Apps.`);
        return;
      }
      api.post(`/apps/${app.app_id}/launch`).catch(() => {});
      window.open(url, "_blank");
      toast.success(`Opened ${app.name} in new tab`);
      return;
    }
    navigate(`/apps/${app.slug || app.app_id}`);
  };

  const persistTileOrder = async (next) => {
    setApps(next);
    try { await api.put("/me/dashboard-order", { ordered_ids: next.map((a) => a.app_id) }); }
    catch { toast.error("Failed to save layout"); }
  };
  // Effective section for an app respecting the user's personal override.
  // Iter 90d — a third bucket: any app carrying a badge other than "New"
  // (i.e., Beta / Roadmap / On Deck / Future) auto-lands in the Roadmap
  // section, which is IP-locked like Internal Only. Explicit user
  // overrides still win.
  const sectionOf = (app) => {
    const ov = sectionOverrides[app.app_id];
    if (ov === "internal" || ov === "shared" || ov === "roadmap") return ov;
    const b = pickTileBadge(app);
    if (b && b.kind !== "new") return "roadmap";
    return app.internal_only ? "internal" : "shared";
  };

  const moveTileToSection = async (appId, targetKey) => {
    if (!editMode) return;
    const app = apps.find((a) => a.app_id === appId);
    if (!app || sectionOf(app) === targetKey) return;
    // Compute the app's default section (matches sectionOf when no override).
    const b = pickTileBadge(app);
    const defaultSection = (b && b.kind !== "new")
      ? "roadmap"
      : (app.internal_only ? "internal" : "shared");
    // If the drop matches the app's global default, clear the override
    // instead of storing a redundant value.
    const nextOverride = targetKey === defaultSection ? "" : targetKey;
    setSectionOverrides((cur) => {
      const copy = { ...cur };
      if (nextOverride) copy[appId] = nextOverride;
      else delete copy[appId];
      return copy;
    });
    try {
      await api.put("/me/section-overrides", { app_id: appId, section: nextOverride });
      const label = targetKey === "internal"
        ? "Internal Only"
        : targetKey === "roadmap"
          ? "Roadmap"
          : "Applications";
      toast.success(`${app.name} is now in ${label} (just for you)`);
    } catch {
      toast.error("Could not save section change");
      // Roll back
      setSectionOverrides((cur) => {
        const copy = { ...cur };
        const prev = sectionOverrides[appId];
        if (prev) copy[appId] = prev;
        else delete copy[appId];
        return copy;
      });
    }
  };

  const onTileDrop = (targetId) => {
    const fromId = dragId.current; dragId.current = null; setDragOverId(null);
    if (!fromId || fromId === targetId) return;
    const fromApp = apps.find((a) => a.app_id === fromId);
    const toApp = apps.find((a) => a.app_id === targetId);
    if (!fromApp || !toApp) return;
    if (sectionOf(fromApp) !== sectionOf(toApp)) { moveTileToSection(fromId, sectionOf(toApp)); return; }
    const next = [...apps];
    const fromIdx = next.findIndex((a) => a.app_id === fromId);
    const toIdx = next.findIndex((a) => a.app_id === targetId);
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    persistTileOrder(next);
  };

  const handleSectionDrop = (key) => {
    if (sectionDrag) { onSectionDrop(key); return; }
    const fromId = dragId.current;
    if (fromId) { dragId.current = null; setDragOverId(null); moveTileToSection(fromId, key); }
  };

  const onSectionDrop = async (target) => {
    const from = sectionDrag; setSectionDrag(null);
    if (!from || from === target) return;
    const next = sectionOrder.filter((s) => s !== from);
    next.splice(next.indexOf(target), 0, from);
    setSectionOrder(next);
    try { await api.put("/me/section-order", { order: next }); toast.success("Section order saved"); }
    catch { toast.error("Failed to save section order"); }
  };

  const greeting = { staff: "Operations overview", partner: "Partner overview", customer: "Your account" }[user?.role]
    || (user?.role === "partner_success" ? "My partner organizations" : "Overview");

  const internalApps = apps.filter((a) => sectionOf(a) === "internal");
  const sharedApps = apps.filter((a) => sectionOf(a) === "shared");
  const roadmapApps = apps.filter((a) => sectionOf(a) === "roadmap");
  const appsFor = (key) =>
    key === "internal" ? internalApps
    : key === "roadmap" ? roadmapApps
    : sharedApps;

  // Iter 90d — remember collapsed sections per user. localStorage key
  // scoped by user_id so multiple accounts on the same browser don't
  // stomp each other. Iter 90f — "Applications" is *always* rendered
  // expanded on page load: the Show/Hide toggle still works within a
  // session but the collapsed flag is never persisted so users don't
  // accidentally hide their primary launch grid.
  const collapseKey = `dash_sections_collapsed_${user?.user_id || "anon"}`;
  const isCollapsed = (k) => {
    if (editMode) return false;
    if (k === "shared") return false;
    return !!sectionCollapsed[k];
  };
  const toggleCollapsed = (k) => {
    setSectionCollapsed((cur) => {
      const next = { ...cur, [k]: !cur[k] };
      try {
        // Never persist Applications' collapsed state — it must open
        // fresh on every visit.
        const forStorage = { ...next };
        delete forStorage.shared;
        localStorage.setItem(collapseKey, JSON.stringify(forStorage));
      } catch { /* noop */ }
      return next;
    });
  };
  const mm = String(Math.floor((logoutIn || 0) / 60)).padStart(2, "0");
  const ss = String((logoutIn || 0) % 60).padStart(2, "0");

  // Iter 90h — super-admin-only quick-edit deep link into Settings → Apps.
  // Iter 90p — ctrl/⌘+click a pencil to toggle multi-select; a bulk edit
  // bar appears when >=2 apps are selected. Regular click still opens
  // the single-app edit dialog.
  const isSuperAdmin = user?.role === "super_admin";
  const [bulkSel, setBulkSel] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const openAppEditor = (appId, e) => {
    if (e && (e.ctrlKey || e.metaKey || e.shiftKey)) {
      setBulkSel((cur) => {
        const next = new Set(cur);
        if (next.has(appId)) next.delete(appId); else next.add(appId);
        return next;
      });
      return;
    }
    navigate(`/apps?edit=${encodeURIComponent(appId)}`);
  };
  const clearBulk = () => setBulkSel(new Set());
  const applyBulk = async (patch) => {
    // patch example: {internal_only: true} or {badge_kind: "beta"}
    const ids = [...bulkSel];
    try {
      await Promise.all(ids.map((id) => api.patch(`/apps/${id}`, patch)));
      toast.success(`Updated ${ids.length} app${ids.length === 1 ? "" : "s"}`);
      // Reload the apps list so tiles reflect new badge/section.
      const r = await api.get("/apps");
      setApps(r.data || []);
      setBulkOpen(false);
      clearBulk();
    } catch (e) {
      toast.error("Bulk update failed — some apps may have partial changes");
    }
  };

  const renderTile = (a) => {
    const isHidden = hidden.includes(a.app_id);
    const canDrag = editMode;
    // Compact + Pill views are read-only display modes — edit mode
    // (drag/hide) always uses the full tile so the affordances stay
    // discoverable.
    if (!editMode && viewMode === "compact")
      return <AppTileCompact key={a.app_id} a={a} sdOpenCount={sdOpenCount} hdOpenCount={hdOpenCount} hdOverdue={hdOverdue} onLaunch={launchApp}
                             isSuperAdmin={isSuperAdmin} onQuickEdit={openAppEditor}
                             isBulkSelected={bulkSel.has(a.app_id)} />;
    if (!editMode && viewMode === "pill")
      return <AppTilePill key={a.app_id} a={a} sdOpenCount={sdOpenCount} hdOpenCount={hdOpenCount} hdOverdue={hdOverdue} onLaunch={launchApp}
                          isSuperAdmin={isSuperAdmin} onQuickEdit={openAppEditor}
                          isBulkSelected={bulkSel.has(a.app_id)} />;
    return (
      <AppTile
        key={a.app_id}
        a={a}
        locked={false}
        editMode={editMode}
        isHidden={isHidden}
        canDrag={canDrag}
        sdOpenCount={sdOpenCount}
        hdOpenCount={hdOpenCount}
        hdOverdue={hdOverdue}
        dragOverId={dragOverId}
        onLaunch={launchApp}
        onDragStart={(e) => { if (canDrag) { dragId.current = a.app_id; try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", a.app_id); } catch { /* noop */ } } }}
        onDragOver={(e) => { if (canDrag) { e.preventDefault(); setDragOverId(a.app_id); } }}
        onDragLeave={() => setDragOverId((p) => (p === a.app_id ? null : p))}
        onDrop={() => { if (canDrag) onTileDrop(a.app_id); }}
        onDragEnd={() => { dragId.current = null; setDragOverId(null); }}
        onHide={hideApp}
        onShow={showApp}
        isSuperAdmin={isSuperAdmin}
        onQuickEdit={openAppEditor}
        isBulkSelected={bulkSel.has(a.app_id)}
      />
    );
  };

  const renderSection = (key) => {
    const isInternal = key === "internal";
    const isRoadmap = key === "roadmap";
    // Iter 114s — Internal Only + Roadmap are internal plumbing. Partners
    // and customers must never see the section, the tiles, or the
    // off-network "Request whitelist" prompt.
    const baseRole = user?.base_role || user?.role;
    const isInternalStaff = baseRole === "staff" || baseRole === "super_admin" || user?.role === "super_admin";
    if ((isInternal || isRoadmap) && !isInternalStaff) return null;
    const offNetwork = (isInternal || isRoadmap) && !access.whitelisted;
    // Off-network, staff still see apps handed to them by name (per-user grant).
    const granted = new Set(user?.custom_permissions || []);
    const list = offNetwork && !isSuperAdmin
      ? appsFor(key).filter((a) => a.permission_module && granted.has(`${a.permission_module}.view`))
      : appsFor(key);
    if (list.length === 0 && !editMode) return null;
    // Roadmap tiles carry the same IP-lock treatment as Internal Only
    // (in-development apps aren't for external eyes); only super admins get
    // the locked card + whitelist request.
    const locked = offNetwork && isSuperAdmin;
    if (offNetwork && !isSuperAdmin && list.length === 0) return null;
    const collapsed = isCollapsed(key);
    const shownTiles = (editMode ? list : list.filter((a) => !hidden.includes(a.app_id)));

    const title = isInternal ? "Internal Only"
      : isRoadmap ? "Roadmap"
      : "Applications";
    // Iter 114r — the IP-lock note is internal plumbing; only super admins see it.
    const isSuper = user?.role === "super_admin";
    const subtitle = isInternal ? (isSuper ? "— restricted by IP location" : "")
      : isRoadmap ? (isSuper ? "— in-development · restricted by IP location" : "— in-development")
      : "";
    const SectionIcon = isInternal ? ShieldCheck
      : isRoadmap ? Compass
      : Globe;
    const iconColor = isInternal ? "text-amber-500"
      : isRoadmap ? "text-emerald-600"
      : "text-primary";

    return (
      <div key={key} className="mb-6" data-testid={`dashboard-section-${key}`}
        onDragOver={(e) => { if (editMode) e.preventDefault(); }} onDrop={() => editMode && handleSectionDrop(key)}>
        <div className="mb-3 flex items-center gap-2"
          draggable={editMode}
          onDragStart={(e) => { if (editMode) { setSectionDrag(key); try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", "section:" + key); } catch { /* noop */ } } }}
          onDragEnd={() => setSectionDrag(null)}>
          {editMode && <GripVertical className="h-4 w-4 cursor-grab text-slate-300" data-testid={`dashboard-section-drag-${key}`} />}
          <SectionIcon className={`h-4 w-4 ${iconColor}`} />
          <h2 className="font-heading text-lg font-semibold text-slate-800" data-testid={`dashboard-section-title-${key}`}>{title}</h2>
          {subtitle && <span className="text-xs text-slate-500" data-testid={`dashboard-section-subtitle-${key}`}>{subtitle}</span>}
          {locked && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700" data-testid="internal-locked-badge">Locked · off-network</span>}
          {!editMode && (
            <button type="button" onClick={() => toggleCollapsed(key)}
                    data-testid={`dashboard-section-collapse-${key}`}
                    aria-label={collapsed ? `Expand ${title}` : `Collapse ${title}`}
                    className="ml-auto inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[11px] font-medium text-slate-500 hover:text-slate-800 hover:border-slate-300 transition">
              {shownTiles.length} app{shownTiles.length === 1 ? "" : "s"}
              {collapsed ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
            </button>
          )}
        </div>
        {collapsed ? null : locked ? (
          <Card className="border-amber-200 bg-amber-50/40 p-5" data-testid={`${key}-locked-card`}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <LockKeyhole className="mt-0.5 h-5 w-5 text-amber-600" />
                <div>
                  <p className="text-sm font-medium text-slate-800">{list.length} {isRoadmap ? "roadmap" : "internal"} app{list.length > 1 ? "s are" : " is"} hidden on this network.</p>
                  <p className="mt-0.5 text-xs text-slate-500">Your IP: <span className="font-mono font-medium text-slate-700" data-testid="my-ip-value">{access.ip || "…"}</span> · {access.can_manage ? "Whitelist it to unlock." : "Ask an admin to whitelist it, or send a request."}</p>
                </div>
              </div>
              <Button size="sm" onClick={whitelistMyIp} data-testid="whitelist-ip-button" className="bg-amber-600 hover:bg-amber-700 text-white">
                <ShieldCheck className="h-4 w-4" /> {access.can_manage ? "Whitelist my IP" : "Request whitelist"}
              </Button>
            </div>
          </Card>
        ) : (
          <div data-testid={`dashboard-grid-${key}-${editMode ? "edit" : viewMode}`}
            className={
              editMode || viewMode === "full"
                ? "grid grid-cols-4 gap-2 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12"
                : viewMode === "compact"
                  ? "grid grid-cols-6 gap-2 sm:grid-cols-10 md:grid-cols-14 lg:grid-cols-18 xl:grid-cols-24"
                  : "grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3"
            }>
            {shownTiles.map((a) => renderTile(a))}
          </div>
        )}
        {!collapsed && editMode && list.length === 0 && !locked && (
          <div className="rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/50 py-6 text-center text-xs text-slate-400" data-testid={`dashboard-section-empty-${key}`}>
            Drag apps here to mark them {title}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <PageHeader
        testid="dashboard-header"
        title={`Welcome, ${user?.name?.split(" ")[0] || "there"}`}
        subtitle={greeting}
      >
        <RoleBadge role={user?.base_role || user?.role} label={user?.role_label} />
      </PageHeader>

      {/* Iter 88q — Global email kill switch. Sits at the very top of
          the Dashboard so ops never has to hunt for it. Backend defaults
          to PAUSED so no external comms leak while config/testing.
          Iter 88r — QA allowlist so staff inboxes still receive live
          template output for verification.
          Iter 106j — Restricted to operators who hold `settings.manage`
          (super_admins + staff granted the perm). Partner admins,
          customers, and staff without the perm never see the banner —
          the pause state is an internal ops signal and shouldn't leak
          outside the org that runs the platform. */}
      {has("settings_notifications.view") && (
        <EmailKillSwitch variant="banner"
                         isSuperAdmin={user?.role === "super_admin"}
                         currentUserEmail={user?.email} />
      )}

      {/* Iter 143 — nudge people off the auto-assigned avatar. */}
      <div className="mb-4"><AvatarNudge testid="dashboard-avatar-nudge" /></div>
      {user?.base_role === "partner" && <PriceChangesCard />}

      {/* Iter 101 — Celebrations banner promoted to the top of the
          dashboard so birthdays and work anniversaries are the first
          thing staff see. Sits below the PageHeader breadcrumb and
          below the EmailKillSwitch alert so it never covers a live
          system notice. Full-width above the 2/3 + 1/3 grid. */}
      {user?.base_role === "staff" && (
        <div className="mb-6" data-testid="dashboard-celebrations-banner">
          <CelebrationsWidget />
        </div>
      )}

      {/* 2/3 + 1/3 layout: apps + status widgets live in the wide column,
          contact details + partner news pin to the right. Collapses to a
          single column below `lg` for tablets/phones. */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3" data-testid="dashboard-two-col">
      <div className="lg:col-span-2 space-y-6">

      {/* Iter 90f — Dashboard Header hero banner (Marketing-editable, clickable).
          Iter 110t — Collapsible + memoized dismissal. localStorage
          key `conduit.dashboard-header.dismissed.v1` holds a hash of
          the CURRENT banner payload (image + link + alt). Whenever
          Marketing swaps any field the hash changes and the banner
          re-appears automatically, so a dismissed announcement can
          never linger. */}
      {info?.dashboard_header?.active && info.dashboard_header.image_url && has("marketing_banner.view") && (() => {
        const dh = info.dashboard_header;
        const bannerKey = `${dh.image_url || ""}|${dh.link_url || ""}|${dh.alt_text || ""}`;
        // Collapse rules:
        //   1. Permanent dismiss (Iter 110t) — { hash === current, until === null }
        //   2. Snooze (Iter 110v)          — { hash === current, until > now }
        // Anything else (mismatched hash OR expired snooze) drops back
        // to expanded, so a new marketing payload OR the clock rolling
        // past the snooze always reopens the banner.
        const now = Date.now();
        const rec  = dashboardHeaderDismissed;
        const hashMatch = rec && rec.hash === bannerKey;
        const snoozed   = hashMatch && typeof rec.until === "number" && rec.until > now;
        const permanent = hashMatch && rec.until === null;
        const collapsed = snoozed || permanent;
        const persist = (val) => {
          try {
            if (val) localStorage.setItem("conduit.dashboard-header.dismissed.v1", JSON.stringify(val));
            else localStorage.removeItem("conduit.dashboard-header.dismissed.v1");
          } catch { /* ignore */ }
          setDashboardHeaderDismissed(val);
        };
        const snoozeFor = (ms) => persist({ hash: bannerKey, until: now + ms });
        const dismissForever = () => persist({ hash: bannerKey, until: null });
        const reopen = () => persist(null);
        if (collapsed) {
          const untilTxt = snoozed
            ? ` (auto-reopens ${new Date(rec.until).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })})`
            : "";
          return (
            <button
              type="button"
              onClick={reopen}
              data-testid="dashboard-header-expand-btn"
              className="flex w-full items-center justify-between gap-2 rounded-xl border border-primary/30 bg-primary/10 px-4 py-2.5 text-xs font-medium text-primary shadow-sm transition hover:border-primary/60 hover:bg-primary/15"
              title={snoozed ? `Snoozed until ${new Date(rec.until).toLocaleString()}` : "Show announcement"}
            >
              <span className="inline-flex items-center gap-2 min-w-0">
                {snoozed ? <Clock className="h-4 w-4 shrink-0" /> : <Newspaper className="h-4 w-4 shrink-0" />}
                <span className="truncate">
                  {dh.alt_text ? `Show announcement: ${dh.alt_text}` : "Show announcement"}
                </span>
                {untilTxt && <span className="text-[10px] opacity-70 shrink-0">{untilTxt}</span>}
              </span>
              <ChevronDown className="h-4 w-4 shrink-0" />
            </button>
          );
        }
        return (
          <div
            className="relative"
            data-testid="dashboard-header-hero-wrap"
          >
            <a
              href={dh.link_url || "#"}
              target="_blank" rel="noopener noreferrer"
              data-testid="dashboard-header-hero"
              className="block rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/40"
              aria-label={dh.alt_text || "Dashboard header — open article"}
            >
              {/* Iter 110z — Marketing banner corners were showing hard
                  white edges from the PNG on top of the page
                  background. `mix-blend-mode: multiply` turns any
                  near-white PNG pixels transparent (works great on
                  page backgrounds that aren't pure white) AND a
                  `mask-image` radial gradient explicitly fades the
                  outer 6% of the image so the corners always blend
                  no matter what the PNG or the page background is.
                  Iter 113 — `multiply` collapses ALL pixels toward
                  black on the near-black dark-mode chrome, making
                  the banner unreadable. The `dashboard-hero-img`
                  class lets `.dark` disable `mix-blend-mode` in CSS
                  and rely on the mask alone. */}
              <img
                src={dh.image_url}
                alt={dh.alt_text || "Dashboard header"}
                className="dashboard-hero-img block h-auto w-full"
                style={{
                  mixBlendMode: "multiply",
                  maskImage: "radial-gradient(ellipse 105% 115% at center, black 82%, transparent 100%)",
                  WebkitMaskImage: "radial-gradient(ellipse 105% 115% at center, black 82%, transparent 100%)",
                }}
                loading="eager"
                data-testid="dashboard-header-hero-image"
              />
            </a>
            {/* Iter 110v — Dismiss menu with snooze options. Uses the
                existing shadcn DropdownMenu for parity with the rest
                of the app. The default click surface is the whole
                banner (opens the article), so we stopPropagation to
                keep the menu from accidentally firing the link. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                  data-testid="dashboard-header-collapse-btn"
                  title="Hide announcement"
                  aria-label="Hide announcement banner"
                  className="absolute right-2 top-2 rounded-full bg-white/85 p-1.5 text-slate-500 shadow-sm ring-1 ring-slate-200 backdrop-blur transition hover:bg-white hover:text-primary"
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Hide this announcement</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={(e) => { e.preventDefault(); snoozeFor(60 * 60 * 1000); }}
                  data-testid="dashboard-header-snooze-1h"
                >
                  <Clock className="h-3.5 w-3.5" /> Snooze 1 hour
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => { e.preventDefault(); snoozeFor(24 * 60 * 60 * 1000); }}
                  data-testid="dashboard-header-snooze-24h"
                >
                  <Clock className="h-3.5 w-3.5" /> Snooze 24 hours
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => { e.preventDefault(); snoozeFor(7 * 24 * 60 * 60 * 1000); }}
                  data-testid="dashboard-header-snooze-7d"
                >
                  <Clock className="h-3.5 w-3.5" /> Snooze 7 days
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={(e) => { e.preventDefault(); dismissForever(); }}
                  data-testid="dashboard-header-dismiss-forever"
                >
                  <BellOff className="h-3.5 w-3.5" /> Hide until Marketing updates it
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      })()}

      {apps.length > 0 && (
        <div className="mb-8" data-testid="dashboard-apps">
          <div className="mb-3 flex items-center justify-end gap-3 flex-wrap">
            {editMode && <span className="hidden sm:inline text-xs text-slate-400">drag tiles or section headers to reorder</span>}
            {!editMode && (
              <div className="inline-flex items-center rounded-full border border-slate-200 bg-white p-0.5 shadow-sm"
                   data-testid="dashboard-view-toggle">
                {[
                  { key: "pill",    icon: List,       label: "List"    },
                  { key: "compact", icon: Rows3,      label: "Compact" },
                ].map(({ key, icon: I, label }) => (
                  <button key={key} onClick={() => setViewMode(key)}
                          data-testid={`dashboard-view-${key}`}
                          title={`${label} view`}
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition ${
                            viewMode === key
                              ? "bg-primary text-primary-foreground shadow-sm"
                              : "text-slate-500 hover:text-slate-800"
                          }`}>
                    <I className="h-3 w-3" /> {label}
                  </button>
                ))}
              </div>
            )}
            {/* Iter 90h — Security chip moved to the global top nav. */}
            <button onClick={() => setEditMode((e) => !e)} data-testid="dashboard-apps-edit" className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
              {editMode ? <><Check className="h-3.5 w-3.5" /> Done</> : <><Pencil className="h-3.5 w-3.5" /> Edit</>}
            </button>
            {has("apps.view") && <button onClick={() => navigate("/apps")} data-testid="dashboard-apps-viewall" className="text-xs font-medium text-primary hover:underline">View all</button>}
          </div>
          {sectionOrder.map((k) => renderSection(k))}
          {editMode && <p className="mt-1 text-xs text-slate-400" data-testid="dashboard-edit-hint">Drag tiles to reorder within a section, or drop them into another section — <span className="font-medium">changes are personal</span> and never affect the platform default (super admins configure defaults in Settings → Apps). Tap the red ✕ to hide, the green + to add back.</p>}
        </div>
      )}

      {/* Auto-logout countdown */}
      <Dialog open={logoutIn !== null} onOpenChange={() => {}}>
        <DialogContent className="max-w-md" data-testid="internal-logout-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700"><LockKeyhole className="h-5 w-5" /> Restricted network</DialogTitle>
            <DialogDescription>
              You tried to open an Internal-Only app from an un-whitelisted network (IP <span className="font-mono">{access.ip}</span>). For security, you&apos;ll be signed out in <span className="font-bold text-slate-800" data-testid="logout-countdown">{mm}:{ss}</span> unless this IP is whitelisted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => logout()} data-testid="logout-now-button">Sign out now</Button>
            <Button onClick={whitelistMyIp} data-testid="dialog-whitelist-button" className="bg-amber-600 hover:bg-amber-700 text-white">
              <ShieldCheck className="h-4 w-4" /> {access.can_manage ? "Whitelist my IP" : "Request whitelist"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {info?.show_partner_news && null /* moved to right column */}

      <div className="space-y-4" data-testid="dashboard-info-cards">
        {/* Iter 101 — CelebrationsWidget moved to the top of the
            dashboard (below PageHeader + EmailKillSwitch) so birthdays
            and work anniversaries lead the page.
            Iter 90c — Commissions + Partner Readiness widgets moved to
            the Partners page; Resume Health moved to the Job Board page. */}
        {/* Uptime Stats moved to the right column above Activity feed. */}
      </div>

      {/* Ops heartbeat moved into the right column below Partner News. */}

      </div>{/* /left column (2/3) */}

      {/* -------- Right column (1/3): contact details + partner news + activity -------- */}
      <div className="space-y-6" data-testid="dashboard-right-col">
        {/* Iter 114w — Support & Escalations board requires `support_board.view`. */}
        {has("support_board.view") && (
        <Card className="border-slate-200 shadow-sm" data-testid="card-escalation">
          <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3">
            <LifeBuoy className="h-4 w-4 text-primary" />
            <h3 className="font-heading text-sm font-semibold text-slate-800">{info?.escalation_label || "Contact Details"}</h3>
          </div>
          <div className="max-h-[420px] overflow-y-auto px-5 py-4">
            {info?.escalation
              ? (/</.test(info.escalation)
                  // Rich HTML content (from the RichTextEditor). Sanitize
                  // then render — `.prose` styles headings, tables, links,
                  // lists so the layout matches the Settings preview.
                  ? <div
                      data-testid="escalation-html"
                      className="prose prose-sm max-w-none text-sm text-slate-700
                                 [&_a]:text-primary [&_a]:underline
                                 [&_table]:w-full [&_table]:text-sm [&_table]:border-collapse
                                 [&_th]:text-left [&_th]:font-semibold [&_th]:text-slate-700
                                 [&_th]:border-b [&_th]:border-slate-200 [&_th]:py-2 [&_th]:px-2
                                 [&_td]:border-b [&_td]:border-slate-100 [&_td]:py-2 [&_td]:px-2 [&_td]:align-top
                                 [&_tr:last-child_td]:border-b-0
                                 [&_h1]:text-base [&_h1]:font-semibold [&_h1]:text-slate-800
                                 [&_h2]:text-sm  [&_h2]:font-semibold [&_h2]:text-slate-800
                                 [&_h3]:text-sm  [&_h3]:font-semibold [&_h3]:text-slate-800
                                 [&_p]:my-1.5 [&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5
                                 [&_ol]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-5"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(info.escalation) }} />
                  // Legacy plain-text — keep the pre-wrap fallback so we
                  // never regress older settings that predate rich text.
                  : <pre data-testid="escalation-plaintext"
                         className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-slate-600">{info.escalation}</pre>)
              : <p className="text-sm text-slate-400">—</p>}
          </div>
        </Card>
        )}

        {info?.show_partner_news && has("partner_news.view") && (
          <Card className="border-slate-200 shadow-sm" data-testid="card-partner-news">
            <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3">
              <Newspaper className="h-4 w-4 text-primary" />
              <h3 className="font-heading text-sm font-semibold text-slate-800">Partner News</h3>
              {isNewsEditor && (
                <button onClick={() => navigate("/settings?tab=news")}
                        data-testid="partner-news-edit-pill"
                        className="ml-auto inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary hover:bg-primary/20 transition">
                  <Pencil className="h-3 w-3" /> Edit
                </button>
              )}
            </div>
            <div className="divide-y divide-slate-100 max-h-[560px] overflow-y-auto">
              {news.length === 0 ? (
                <div className="px-5 py-8 text-center text-sm text-slate-400">No news yet.</div>
              ) : (
                news.map((n) => (
                  <NewsArticle key={n.announcement_id} n={n} isEditor={isNewsEditor} isPartner={user?.base_role === "partner"} />
                ))
              )}
            </div>
          </Card>
        )}

        {user?.base_role === "staff" && <ReleaseNotesCard />}

        {/* Uptime Stats moved to the top-bar status dropdown (Iter 90b). */}

        {/* Ops heartbeat lives in the top-nav shortcut → Settings → Activity Logs. */}
      </div>

      </div>{/* /2-col grid */}

      {/* Iter 90p — floating bulk-edit bar for ctrl-clicked pencils. */}
      {isSuperAdmin && bulkSel.size > 0 && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2"
             data-testid="dashboard-bulk-bar">
          <div className="flex items-center gap-3 rounded-full border border-indigo-200 bg-white px-4 py-2 shadow-lg">
            <span className="text-sm font-medium text-slate-700">
              <span className="font-bold text-indigo-600" data-testid="dashboard-bulk-count">{bulkSel.size}</span>
              {" "}app{bulkSel.size === 1 ? "" : "s"} selected
            </span>
            <button onClick={() => setBulkOpen(true)}
                    data-testid="dashboard-bulk-edit-btn"
                    className="rounded-full bg-indigo-600 px-3 py-1 text-xs font-semibold text-white hover:bg-indigo-500">
              Bulk edit…
            </button>
            <button onClick={clearBulk}
                    data-testid="dashboard-bulk-clear"
                    className="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:bg-slate-50">
              Clear
            </button>
          </div>
        </div>
      )}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent data-testid="dashboard-bulk-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk edit — {bulkSel.size} app{bulkSel.size === 1 ? "" : "s"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-xs text-slate-500">Applies to every selected tile. Anything else stays as-is.</p>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => applyBulk({ badge_kind: "beta" })}
                      data-testid="dashboard-bulk-badge-beta">Set badge → BETA</Button>
              <Button variant="outline" onClick={() => applyBulk({ badge_kind: "roadmap" })}
                      data-testid="dashboard-bulk-badge-roadmap">Set badge → ROADMAP</Button>
              <Button variant="outline" onClick={() => applyBulk({ badge_kind: "on_deck" })}
                      data-testid="dashboard-bulk-badge-ondeck">Set badge → ON DECK</Button>
              <Button variant="outline" onClick={() => applyBulk({ badge_kind: "future" })}
                      data-testid="dashboard-bulk-badge-future">Set badge → FUTURE</Button>
              <Button variant="outline" onClick={() => applyBulk({ badge_kind: "new" })}
                      data-testid="dashboard-bulk-badge-new">Set badge → NEW</Button>
              <Button variant="outline" onClick={() => applyBulk({ badge_kind: null })}
                      data-testid="dashboard-bulk-badge-clear">Clear badge</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}

// --------------------------------------------------------------------------
// NewsArticle — Iter 111. Renders one announcement card + fires the
// read-receipt POST when the card scrolls into view for a partner-role
// reader (once per session per article). Editors (super_admin or
// team=Marketing staff) get an inline "seen by N partners" pill so
// Marketing can eyeball engagement without opening a report.
// --------------------------------------------------------------------------
function NewsArticle({ n, isEditor, isPartner }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!isPartner || !n?.announcement_id) return;
    const key = `nv_seen_${n.announcement_id}`;
    if (sessionStorage.getItem(key)) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        io.disconnect();
        sessionStorage.setItem(key, "1");
        // Fire-and-forget — engagement tracking should never surface
        // a toast or block the reader on a network failure.
        api.post(`/announcements/${n.announcement_id}/view`).catch(() => {});
      }
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, [isPartner, n?.announcement_id]);
  const seen = Number.isFinite(n?.seen_by_partners) ? n.seen_by_partners : null;
  return (
    <div ref={ref} className="px-5 py-4" data-testid={`news-${n.announcement_id}`}>
      <div className="flex items-center gap-2">
        {n.pinned && <Pin className="h-3.5 w-3.5 text-primary" />}
        <h4 className="font-semibold text-slate-800">{n.title}</h4>
        {isEditor && seen !== null && (
          <span
            className="ml-auto inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-medium text-slate-500"
            data-testid={`news-seen-pill-${n.announcement_id}`}
            title={`${seen} partner organisation${seen === 1 ? " has" : "s have"} opened this article`}
          >
            <Eye className="h-3 w-3" /> Seen by <b className="text-slate-700">{seen}</b> partner{seen === 1 ? "" : "s"}
          </span>
        )}
      </div>
      {n.body && <div className="prose prose-sm mt-1 max-w-none text-sm text-slate-600 [&_a]:text-primary [&_a]:underline [&_img]:rounded-lg [&_img]:max-w-full [&_img]:h-auto" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(n.body) }} />}
      <div className="mt-1.5 text-xs text-slate-400">{n.author} · {new Date(n.created_at).toLocaleDateString()}</div>
    </div>
  );
}

